import React, { FC, memo, useCallback, useState } from "react";
import { ButtonColor, ButtonSize, ButtonVariant } from "enums/Button";
import { Map } from "immutable";
import { isMobile } from "react-device-detect";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { AddonsMethodsResponseDto, CartResponseDto } from "types/api/cart";
import { ShopsResponseDto } from "types/api/shipments";
import Button from "../../../../components/Button/Button";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import { useDemoMode } from "../../../../contexts/DemoModeContext";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import useItemsViewPreferences from "../../../../hooks/useItemsViewPreferences";
import useProgressDelivery from "../../../../hooks/useProgressDelivery";
import injectReducer from "../../../../reduxFolder/injectReducer";
import { getCart } from "../../../../reduxFolder/reducers/cart";
import outgoingReducer from "../../../../reduxFolder/reducers/outgoing";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import { getAddonsMethods } from "../../../../reduxFolder/reducers/services";
import storageReducer, {
  addParcelsToCart,
  getShops,
} from "../../../../reduxFolder/reducers/storage";
import {
  cartSelectCart,
  cartSelectCartItems,
  cartSelectIsEmpty,
  cartSelectLoading,
} from "../../../../reduxFolder/selectors/cartSelectors";
import {
  authSelectFfid,
  authSelectUnits,
} from "../../../../reduxFolder/selectors/globalSelectors";
import { servicesSelectAddonsAll } from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  storageSelectInventoryItemsGroups,
  storageSelectInventoryItemsSearch,
  storageSelectLoading,
  storageSelectParams,
  storageSelectShops,
  storageSelectTotalAmountInventoryItems,
} from "../../../../reduxFolder/selectors/storageSelectors";
import { StorageButton } from "../../../../styles/parcelsStyles";
import { breakpoints } from "../../../../styles/styles";
import { OpenRightPanel } from "../../../../types/common/rightPanel";
import InventoryList from "../../../Inventory/components/InventoryList";
import { InventoryListPlaceholder } from "../../../Inventory/components/InventoryListPlaceholder";
import CartInfo from "../CartInfo/CartInfo";
import DemoInventoryItems from "../DemoShipments/DemoInventoryItems";
import { ResetFilterButton } from "styles/searchFilterStyles";
import SelectedInfo from "../SelectedInfo/SelectedInfo";
import {
  Buttons,
  ButtonsWrapper,
  SelectAllSwitchWrapper,
  StorageHeading,
  StyledGrid,
  StyledListHeader,
  StyledListHeaderTitle,
  ViewControlsWrapper,
  Wrapper,
} from "./InventoryItems.styles";
import ViewModeControl from "./ViewModeControl";

interface InventoryItemsProps {
  isLoading: boolean;
  isCartLoading: boolean;
  units: string;
  userFfid: string;
  isCartEmpty: boolean;
  detailedShipment: any;
  cart: any;
  cartItems: any;
  openRightPanel: OpenRightPanel;
  shops: any;
  addons: any;
  items: any;
  itemGroups: any;
  addParcelsToCart: (data: any) => void;
  getCart: () => Promise<CartResponseDto>;
  getShops: () => Promise<ShopsResponseDto[]>;
  getAddonsMethods: () => Promise<AddonsMethodsResponseDto[]>;
  onPagination: (group: string) => void;
  totalAmount: number;
  isSearchDirty: boolean;
  hasAnyFiltersApplied: boolean;
  handleReset: () => void;
  handleResetCompact: () => void;
}

const InventoryItems: FC<InventoryItemsProps> = ({
  units,
  isLoading,
  isCartLoading,
  addParcelsToCart,
  getShops,
  shops,
  getCart,
  isCartEmpty,
  userFfid,
  getAddonsMethods,
  addons,
  cartItems: cartItemsProp,
  cart,
  openRightPanel,
  items,
  itemGroups,
  onPagination,
  totalAmount,
  isSearchDirty,
  hasAnyFiltersApplied,
  handleReset,
  handleResetCompact,
}) => {
  const [, setAccess] = useProgressDelivery(userFfid);
  const { t } = useTranslation("common");
  const { isDemoEnabled } = useDemoMode();
  const [selectedItems, setSelectedItems] = useState<any>({});
  const [viewPreferences, setViewPreferences] = useItemsViewPreferences();
  const navigate = useNavigate();

  const hasItems = !!items.size;
  // Settled-empty, unfiltered items search → show the demo shipment's sample
  // items (mirrors the DemoShipments gate on the parcels list).
  const showDemoItems =
    isDemoEnabled && !hasItems && !isLoading && !hasAnyFiltersApplied;
  const cartItems = cartItemsProp.toJS();
  const getIsItemInCart = (itemId: string | number) => {
    return cartItems.some((item: any) => item.id === itemId);
  };

  const selectedParcels = Object.values(selectedItems).filter(
    (item: any) => item.isSelectedState && !getIsItemInCart(item.id),
  );
  const selectableItems = items
    .toJS()
    .filter(
      (item: any) =>
        !getIsItemInCart(item.id) &&
        ["Active", "InContainer", "ContainerPacked"].indexOf(item.status) !==
          -1,
    );
  const selectedParcelsWeight = selectedParcels
    .reduce(
      (sum: number, item: any) =>
        sum + items.find((x: any) => x.get("id") === item.id).get("weight"),
      0,
    )
    .toFixed(2);
  const totalSelectableItemsWeight = selectableItems
    .reduce((sum: number, item: any) => sum + item.weight, 0)
    .toFixed(2);
  const hasSelected = selectedParcels.length > 0;
  const isAllSelected = selectedParcels.length === selectableItems.length;

  useFetchOnceEffect(!isCartLoading, getCart);
  useFetchOnceEffect(!shops.size, getShops);
  useFetchOnceEffect(!addons.size, getAddonsMethods);

  const toggleAllItemsSelectState = (nextValue: any) => {
    const nextSelectedItems: any = {};
    selectableItems.forEach((item: any) => {
      nextSelectedItems[item.id] = selectedItems[item.id]
        ? {
            ...selectedItems[item.id],
            isSelectedState: nextValue,
          }
        : {
            id: item.id,
            isSelectedState: nextValue,
            ...(item.quantity > 1 && { selectedQuantity: 1 }),
          };
    });
    setSelectedItems(nextSelectedItems);
  };

  const handleSelectAll = () => {
    toggleAllItemsSelectState(true);
  };

  const handleDeselectAll = () => {
    toggleAllItemsSelectState(false);
  };

  const handleSelectItems = (items: any) => {
    setSelectedItems((prevState: any) => ({ ...prevState, ...items }));
  };

  const handleSend = useCallback(() => {
    if (!hasSelected && !isCartEmpty) {
      if (isMobile && window.innerWidth <= +breakpoints.xs) {
        openRightPanel(
          "NOT_FINISHED",
          Map({
            detailedItemID: cart.get("id"),
            parcel: cart,
          }),
        );
      } else {
        navigate("/shipping");
      }
      return;
    }

    const retVal = {
      id: selectedParcels.map((item: any) => item.id),
      decant_ids: selectedParcels
        .filter((x: any) => x.selectedQuantity)
        .reduce((accumulator: any, item: any) => {
          return { ...accumulator, [item.id]: item.selectedQuantity };
        }, {}),
      useCartItemsApi: true,
      shipmentId: null,
    };
    addParcelsToCart(retVal);
    setSelectedItems({});
    if (isCartEmpty) {
      setAccess("initialSteps");
    }
  }, [
    selectedParcels,
    addParcelsToCart,
    hasSelected,
    isCartEmpty,
    setAccess,
    cart,
    openRightPanel,
  ]);

  return (
    <Wrapper>
      <StyledGrid fluid>
        <StorageHeading>
          {/* Gate on data presence, not `isLoading`, so the title mirrors the
              shipments view: it stays visible with the persisted count/weight
              during a re-fetch (stale-while-revalidate) instead of blinking
              out. The parent Storage renders the LoadingBar for both views.
              While the demo items show, `hasItems` is false by construction,
              so the real-data header stays hidden. */}
          {hasItems && (
            <StyledListHeader>
              {selectableItems.length > 0 && (
                <Visible xs>
                  <SelectAllSwitchWrapper>
                    <Checkbox
                      checked={isAllSelected}
                      onChange={() =>
                        isAllSelected ? handleDeselectAll() : handleSelectAll()
                      }
                    />
                  </SelectAllSwitchWrapper>
                </Visible>
              )}
              <StyledListHeaderTitle>
                {t("parcels.total", { amount: totalAmount })}
                {selectableItems.length > 0 && (
                  <span>
                    {` / ${totalSelectableItemsWeight} ${t(
                      `units.${units}.primaryShort`,
                    )}`}
                  </span>
                )}
              </StyledListHeaderTitle>
              {selectableItems.length > 0 && (
                <Hidden xs>
                  {isAllSelected ? (
                    <StorageButton onClick={handleDeselectAll}>
                      {t("common.deselect")}
                    </StorageButton>
                  ) : (
                    <StorageButton onClick={handleSelectAll}>
                      {t("common.selectAll")}
                    </StorageButton>
                  )}
                  {isSearchDirty && (
                    <ResetFilterButton
                      onClick={handleReset}
                    >
                      {t("parcels.search.resetFilter")}
                    </ResetFilterButton>
                  )}
                </Hidden>
              )}
              <ViewControlsWrapper>
                <ViewModeControl
                  mode={viewPreferences.mode}
                  columns={viewPreferences.columns}
                  onModeChange={(mode) => setViewPreferences({ mode })}
                  onColumnsChange={(columns) => setViewPreferences({ columns })}
                />
              </ViewControlsWrapper>
              <Visible xs>
                {isSearchDirty && (
                  <ResetFilterButton
                    onClick={handleResetCompact}
                  >
                    {t("parcels.search.resetFilter")}
                  </ResetFilterButton>
                )}
              </Visible>
            </StyledListHeader>
          )}
        </StorageHeading>
        {hasItems && (
          <InventoryList
            units={units}
            itemGroups={itemGroups}
            selectedItems={selectedItems}
            disabled={isCartLoading}
            getIsItemInCart={getIsItemInCart}
            handleSelectItems={handleSelectItems}
            shipment={Map()}
            onPagination={onPagination}
            isInventoryItemsSearchVariant
            viewMode={viewPreferences.mode}
            tileColumns={viewPreferences.columns}
          />
        )}
        {showDemoItems && <DemoInventoryItems />}
        {!hasItems && !isLoading && !showDemoItems && (
          <InventoryListPlaceholder />
        )}
        {(hasSelected || !isCartEmpty) && (
          <ButtonsWrapper>
            <Buttons $singleRow={!hasSelected}>
              {hasSelected ? (
                <SelectedInfo
                  weight={selectedParcelsWeight}
                  selectedParcelsNumber={selectedParcels.length}
                />
              ) : (
                <CartInfo />
              )}
              <Button
                variant={ButtonVariant.Filled}
                color={ButtonColor.Primary}
                size={ButtonSize.Large}
                disabled={isCartLoading}
                onClick={handleSend}
              >
                {hasSelected ? t("common.addToCart") : t("common.continue")}
              </Button>
              {hasSelected && (
                <StorageButton
                  disabled={isCartLoading}
                  onClick={handleDeselectAll}
                >
                  {t("common.deselect")}
                </StorageButton>
              )}
            </Buttons>
          </ButtonsWrapper>
        )}
      </StyledGrid>
    </Wrapper>
  );
};

const withConnect = connect(
  createStructuredSelector({
    userFfid: authSelectFfid,
    isLoading: storageSelectLoading,
    isCartLoading: cartSelectLoading,
    params: storageSelectParams,
    shops: storageSelectShops,
    units: authSelectUnits,
    cart: cartSelectCart,
    cartItems: cartSelectCartItems,
    isCartEmpty: cartSelectIsEmpty,
    addons: servicesSelectAddonsAll,
    items: storageSelectInventoryItemsSearch,
    itemGroups: storageSelectInventoryItemsGroups,
    totalAmount: storageSelectTotalAmountInventoryItems,
  }),
  {
    addParcelsToCart,
    getCart,
    getShops,
    getAddonsMethods,
    openRightPanel,
  },
);

const withStorageReducer = injectReducer({
  key: "storage",
  reducer: storageReducer,
});

const withOutgoingReducer = injectReducer({
  key: "outgoing",
  reducer: outgoingReducer,
});

const enhance = compose(
  withStorageReducer,
  withOutgoingReducer,
  withConnect,
  memo,
);

export default enhance(InventoryItems);
